import Button from '@material-ui/core/Button';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import { Alert } from '@material-ui/lab';
import { FunctionComponent, memo } from 'react';
import { useAuthUser } from '../../../../../contexts/AuthProvider/hooks';
import ButtonProgress from '../../../../../eliot-components/src/ButtonProgress/ButtonProgress';
import { useConfig } from '../../../../../eliot-components/src/ConfigProvider/hooks';
import {
  useGetTargetSystemsQuery,
  useUpdateOrgMutation,
} from '../../../../../store/api-slice';
import { useSnackbar } from 'notistack';
import { parseErrorMessage } from '../../../../../eliot-components/src/utils/helpers';

interface Props {
  onClose(): void;
  isOpen: boolean;
  orgId: string;
}

const SelectWebhookDialog: FunctionComponent<Props> = memo(
  ({ isOpen, onClose, orgId }) => {
    const config = useConfig();
    const { authUser } = useAuthUser();
    const { enqueueSnackbar } = useSnackbar();
    const [updateOrg, { isLoading: isSaving }] = useUpdateOrgMutation();

    const org = authUser.orgs.find((o) => o.id === orgId);

    const { data: availableTargetSystems = [] } = useGetTargetSystemsQuery(
      { apiBaseURL: config.apiBaseURL, orgs: [orgId] },
      { skip: !isOpen || !orgId }
    );

    const defaultWebhooks = availableTargetSystems.filter((ts) => ts.isDefault);

    const handleEnable = () => {
      if (!org) return;
      updateOrg({
        apiBaseURL: config.apiBaseURL,
        org: { id: org.id, name: org.name, useDefaultWebhooks: true },
      }).then((resp: any) => {
        if (!resp.error) {
          enqueueSnackbar('Default webhooks enabled', { variant: 'success' });
          onClose();
          return;
        }
        enqueueSnackbar(parseErrorMessage(resp.error), { variant: 'error' });
      });
    };

    return (
      <Dialog aria-labelledby="dialog-title" open={isOpen} onClose={onClose}>
        <DialogTitle id="dialog-title">Enable Default Webhooks</DialogTitle>
        <DialogContent style={{ minWidth: '360px' }}>
          {defaultWebhooks.length === 0 ? (
            <Alert severity="warning">
              No default webhooks are configured for this organization. Set
              default webhooks on the Webhooks page first.
            </Alert>
          ) : (
            <>
              <Typography variant="body2" gutterBottom>
                All devices in this organization will automatically use the
                following default webhooks for routing:
              </Typography>
              <List dense disablePadding>
                {defaultWebhooks.map((ts) => (
                  <ListItem key={ts.id} disableGutters>
                    <ListItemText primary={ts.name} secondary={ts.url} />
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancel</Button>
          <ButtonProgress
            variant="contained"
            color="primary"
            isLoading={isSaving}
            disabled={defaultWebhooks.length === 0}
            onClick={handleEnable}
          >
            Enable
          </ButtonProgress>
        </DialogActions>
      </Dialog>
    );
  }
);

export default SelectWebhookDialog;
